import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Job } from '@/types';
import { parseJobSlug } from '@/utils/jobSlug';

export function useJobDetail(routeParam: string | undefined) {
  const queryClient = useQueryClient();

  // Accept full UUID or slug. parseJobSlug returns the 12-char short_id
  // (or the original string when the route already looked like one).
  const parsed = routeParam ? parseJobSlug(routeParam) : null;
  const isFullUuid =
    !!routeParam &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(routeParam);

  const query = useQuery({
    queryKey: ['job', routeParam],
    queryFn: async (): Promise<Job | null> => {
      if (!routeParam || !parsed) return null;

      const builder = supabase.from('jobs').select('*');
      const { data, error } = isFullUuid
        ? await builder.eq('id', routeParam).maybeSingle()
        : await (builder as any).eq('short_id', parsed.shortId).maybeSingle();
      
      if (error) {
        console.error('Error fetching job:', error);
        throw error;
      }
      
      if (!data) return null;
      
      const mapped = {
        id: data.id,
        type: 'job',
        url: data.apply_link || '',
        created_at: data.post_date,
        company: data.company,
        company_url: null,
        location: data.display_location || data.location,
        display_location: data.display_location ?? null,
        display_salary: (data as any).display_salary ?? null,
        title: data.title,
        description: data.job_full_text || '',
        how_to_apply: data.apply_link,
        company_logo: data.logo,
        salary: (data as any).display_salary ?? '',
        is_remote: data.is_remote,
        is_featured: data.is_featured || false,
        logo: data.logo || undefined,
        apply_link: data.apply_link || undefined,
        post_date: new Date(data.post_date),
        hours: data.hours || 'Full-Time',
        jobFullText: data.job_full_text || undefined,
        formattedJobContent: data.formatted_job_content || undefined,
        applyLink: data.apply_link || undefined,
        postDate: new Date(data.post_date),
        isRemote: data.is_remote,
        isFeatured: data.is_featured || false,
        seniority: (data as any).seniority ?? null,
        department: (data as any).department ?? null,
        company_description: (data as any).company_description ?? null,
        board_source: (data as { board_source?: string | null }).board_source ?? null,
        founded_year: (data as any).founded_year ?? null,
        // JobAlertSignup sets alerts for the job's canonical role, so the
        // field has to survive this mapping; the cast below keeps the
        // excess-property check quiet.
        canonical_role: (data as any).canonical_role ?? null,
        // 'state_only' or 'country_only' when the listing names no city; the
        // page says so next to the location.
        location_granularity: (data as any).location_granularity ?? null,
        salary_min_usd: (data as any).salary_min_usd ?? null,
        salary_max_usd: (data as any).salary_max_usd ?? null,
        enriched_data: (data as any).enriched_data ?? null,
        enriched_at: (data as any).enriched_at ?? null,
        // A closed posting still loads (people arrive from old links and from
        // answer engines); the page shows ClosedJobNotice instead of treating
        // it as live. Without these two the enrichment guard below never fired
        // either, since job.is_archived was always undefined.
        is_archived: Boolean((data as any).is_archived),
        archived_at: (data as any).archived_at ?? null,
      };
      return mapped as unknown as Job;
    },
    enabled: !!routeParam,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fire-and-forget enrichment. Skip when the job already carries the current
  // schema, and skip archived or duplicate postings outright: an expired page
  // still loads (crawlers mostly) and each load used to cost a model call for
  // bullets nobody needs. The function enforces the same rule server-side.
  // On success we write enriched_data straight into the React Query cache so
  // the TL;DR card renders without waiting for a refetch round-trip.
  const job = query.data;
  useEffect(() => {
    if (!job?.id) return;
    if ((job.enriched_data?.schema_version ?? 0) >= 2) return;
    if (job.is_archived || job.is_duplicate) return;

    supabase.functions
      .invoke('enrich-job-detail', { body: { job_id: job.id } })
      .then(({ data, error }) => {
        if (error) return;
        if (data?.enriched_data) {
          queryClient.setQueryData(['job', routeParam], (prev: Job | null | undefined) =>
            prev ? { ...prev, enriched_data: data.enriched_data } : prev
          );
        } else {
          queryClient.invalidateQueries({ queryKey: ['job', routeParam] });
        }
      })
      .catch((e) => console.warn('enrichment failed', e));
  }, [job?.id, job?.enriched_data?.schema_version, job?.is_archived, job?.is_duplicate, queryClient, routeParam]);

  return Object.assign(query, { isLegacyUuid: isFullUuid });
}

export function useRelatedJobs(currentJobId: string | undefined, jobTitle: string | undefined, limit: number = 4) {
  return useQuery({
    queryKey: ['relatedJobs', currentJobId, jobTitle],
    queryFn: async (): Promise<Job[]> => {
      if (!currentJobId || !jobTitle) return [];
      
      // Extract key terms from job title for matching
      const keywords = jobTitle
        .toLowerCase()
        .replace(/[^a-z\s]/g, '')
        .split(' ')
        .filter(word => word.length > 3)
        .slice(0, 2);
      
      // is_archived = false is the predicate every partial "live jobs" index
      // carries (ix_jobs_sitemap_order, idx_jobs_board_recent). Without it the
      // planner can use none of them, so this scanned 265,940 rows and read
      // 381 MB from disk to return 12, evicting 3,280 dirty pages from a 256 MB
      // buffer cache on each of ~418k calls. Measured: 3,468 ms -> 0.281 ms,
      // 48,750 disk reads -> 0. It is also a correctness fix, since "related
      // jobs" was otherwise free to surface expired postings.
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .neq('id', currentJobId)
        .eq('is_duplicate', false)
        .eq('is_archived', false)
        .order('post_date', { ascending: false })
        .limit(limit * 3); // Fetch more to filter
      
      if (error) {
        console.error('Error fetching related jobs:', error);
        return [];
      }
      
      // Filter for jobs with similar titles
      const related = data
        .filter(job => {
          const title = job.title.toLowerCase();
          return keywords.some(keyword => title.includes(keyword));
        })
        .slice(0, limit);
      
      // If not enough related, fill with recent jobs
      if (related.length < limit) {
        const remaining = data
          .filter(job => !related.find(r => r.id === job.id))
          .slice(0, limit - related.length);
        related.push(...remaining);
      }
      
      return related.map(job => ({
        id: job.id,
        type: 'job',
        url: job.apply_link || '',
        created_at: job.post_date,
        company: job.company,
        company_url: null,
        location: job.display_location || job.location,
        display_location: job.display_location ?? null,
        display_salary: (job as any).display_salary ?? null,
        title: job.title,
        description: job.job_full_text || '',
        how_to_apply: job.apply_link,
        company_logo: job.logo,
        salary: (job as any).display_salary ?? '',
        is_remote: job.is_remote,
        is_featured: job.is_featured || false,
        logo: job.logo || undefined,
        apply_link: job.apply_link || undefined,
        post_date: new Date(job.post_date),
        hours: job.hours || 'Full-Time',
        applyLink: job.apply_link || undefined,
        postDate: new Date(job.post_date),
        isRemote: job.is_remote,
        isFeatured: job.is_featured || false,
        seniority: (job as any).seniority ?? null,
        department: (job as any).department ?? null,
        company_description: (job as any).company_description ?? null,
        founded_year: (job as any).founded_year ?? null,
        enriched_data: (job as any).enriched_data ?? null,
        enriched_at: (job as any).enriched_at ?? null,
      }));
    },
    enabled: !!currentJobId && !!jobTitle,
    staleTime: 5 * 60 * 1000,
  });
}
